import { DateTime } from 'luxon';
import { Box, styled, Typography } from '@mui/material';
import type { LevelTableAnyColumn } from 'redesign';
import { CalendarGridRow, CalendarModes, dayAccessors } from '../calendar-grid.types';

export const getDateColumns = <
  ColumnDefinition extends CalendarGridRow<DateCellData>,
  DateCellData extends object[],
>(
  calendarMode: CalendarModes,
  startDate: DateTime,
  cellFn: ({ value }: { value: DateCellData | undefined }) => JSX.Element,
  headerFn?: (...args: any[]) => JSX.Element
): LevelTableAnyColumn<ColumnDefinition>[] => {
  const daysInMonth = startDate.daysInMonth;

  const createDayColumns = (
    daysInMonth: number,
    cellFn: ({ value }: { value: DateCellData | undefined }) => JSX.Element,
    headerFn: (date: DateTime) => JSX.Element
  ) => {
    const columns: LevelTableAnyColumn<ColumnDefinition>[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const date = startDate.set({ day: i });
      columns.push({
        width: '30px',
        maxWidth: 1,
        accessor: dayAccessors[i - 1],
        Cell: ({ value }: { value: DateCellData | undefined }) => cellFn({ value }),
        Header: headerFn(date),
      });
    }
    return columns;
  };

  if (!headerFn) {
    headerFn = (date: DateTime) => {
      return (
        <StyledBox>
          <HeaderTypography variant="inherit">{date.toFormat('EEE')}</HeaderTypography>
          <HeaderTypography variant="inherit">{date.day}</HeaderTypography>
        </StyledBox>
      );
    };
  }

  if (calendarMode === 'month') {
    return createDayColumns(daysInMonth, cellFn, headerFn);
  }

  throw new Error(`Unsupported calendar mode: ${calendarMode}`);
};

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  textAlign: 'center',
  color: theme.palette.lwPrimaryAccentDark?.[40] ?? 'lightblue',
}));
